import { ToggleButtonGroup } from '@mui/material';
import { CellContext, ColumnDef, Row } from '@tanstack/react-table';
import { StyledToggleButton } from 'components/StyledToggleButton';
import { DataTable } from 'components/table/DataTable';
import { createMeasureCell } from 'components/table/DataTableUtil';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import {
  DataEntryRowRecord,
  getMeasureEntryValue,
  MeasureEntry,
  useDataEntriesInTables,
} from 'components/table/useDataEntriesInTables';
import { MeasureFormat } from 'components/measure-unit/MeasureFormat';
import {
  getReferenceMarkdownRate,
  MARKDOWN_VS_CATEGORY_CODE,
  REFERENCE_MARKDOWN_VALUE,
  REFERENCE_ORIGINAL_MERCHANDISE_VALUE,
  toMarkdownVsCategoryMeasure,
} from 'components/price/priceMeasures';
import { useDataRequest } from 'hooks/useDataRequest';
import { Attribute, AttributeCode, Measure, useModelService } from 'services/model/model.service';
import { articleLink, productLink, styleLink } from 'pages/entities/entityhelper';
import { attributeToToggleTranslationKey } from 'pages/entities/entities';
import { usePriceBreakdownRows } from 'pages/entities/queryHooks';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Filters } from 'services/data.service';
import { useMemo, useState } from 'react';

interface PriceBreakdownTableProps {
  filterDecorator: (filter: Filters) => Filters;
  availableTables: Attribute[];
  isCategoryEntity: boolean;
}

interface PriceBreakdownRow extends DataEntryRowRecord {
  [MARKDOWN_VS_CATEGORY_CODE]: MeasureEntry;
  [REFERENCE_MARKDOWN_VALUE]?: MeasureEntry;
  [REFERENCE_ORIGINAL_MERCHANDISE_VALUE]?: MeasureEntry;
  referenceCategory: string;
}

const createMarkdownVsReferenceCell = (
  markdownVsCategory: Measure,
  avgPriceMarkdown: Measure,
  categoryAttribute: Attribute,
  referenceLabelId: string
) =>
  function renderMarkdownVsCategoryCell(context: CellContext<PriceBreakdownRow, MeasureEntry>) {
    const referenceMarkdownRate = getReferenceMarkdownRate({
      [REFERENCE_MARKDOWN_VALUE]: getMeasureEntryValue(context.row.original[REFERENCE_MARKDOWN_VALUE] ?? 0),
      [REFERENCE_ORIGINAL_MERCHANDISE_VALUE]: getMeasureEntryValue(
        context.row.original[REFERENCE_ORIGINAL_MERCHANDISE_VALUE] ?? 0
      ),
    });
    const category = categoryAttribute.getValue({ prdCategory: context.row.original.referenceCategory });
    const markdownVsReference = getMeasureEntryValue(context.getValue());

    return (
      <div className="flex flex-col gap-1">
        <span className={markdownVsReference > 0.05 ? 'text-coral-500' : undefined}>
          {createMeasureCell(context, markdownVsCategory, true, true, { isDiff: true })}
        </span>
        <span className="text-xs text-gray-400">
          <FormattedMessage
            id={referenceLabelId}
            values={{
              category,
              value: <MeasureFormat value={Math.abs(referenceMarkdownRate)} unit={avgPriceMarkdown.metric.unit} />,
            }}
          />
        </span>
      </div>
    );
  };

export function PriceBreakdownTable({ filterDecorator, availableTables, isCategoryEntity }: PriceBreakdownTableProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { getAttribute, getMeasures } = useModelService();
  const {
    createAttributeColumn,
    createMeasureColumn,
    dataEntryToDataEntryRowRecord,
    getAttributeArrayForLeadAttribute,
  } = useDataEntriesInTables();
  const [tableAttribute, setTableAttribute] = useState<AttributeCode>(availableTables[0].code);
  const requestData = useDataRequest();
  const adjustedRequestData = { ...requestData, filters: filterDecorator(requestData.filters) };
  const { data, isError } = usePriceBreakdownRows(tableAttribute, adjustedRequestData);

  const [avgSoldPrice, originalMerchandisePrice, avgPriceMarkdown, soldAtOriginalPrice, soldItems] = getMeasures([
    'itemSlsGmvValExtOD',
    'itemSlsOmvValExtOD',
    'itemSlsMkdRateExtOD',
    'slsAtOmvQtyRateOD',
    'slsQtyOD',
  ]);
  const markdownVsCategory = useMemo(() => toMarkdownVsCategoryMeasure(avgPriceMarkdown), [avgPriceMarkdown]);
  const categoryAttribute = getAttribute('prdCategory');
  const markdownVsReferenceLabelId = isCategoryEntity ? 'price.table.referenceAll' : 'price.table.reference';
  const markdownVsReferenceTitleId = isCategoryEntity ? 'price.kpi.markdownVsAll' : 'price.kpi.markdownVsCategory';
  const markdownVsCategoryCell = useMemo(
    () =>
      createMarkdownVsReferenceCell(
        markdownVsCategory,
        avgPriceMarkdown,
        categoryAttribute,
        markdownVsReferenceLabelId
      ),
    [markdownVsCategory, avgPriceMarkdown, categoryAttribute, markdownVsReferenceLabelId]
  );

  const rows: PriceBreakdownRow[] = (data?.sets.rows ?? []).map(entry => {
    const value = markdownVsCategory.getValue(entry.values);
    return {
      ...dataEntryToDataEntryRowRecord(entry),
      referenceCategory: entry.attributes.prdCategory ?? '',
      [MARKDOWN_VS_CATEGORY_CODE]: entry.compareValues
        ? { value, compareValue: markdownVsCategory.getValue(entry.compareValues) }
        : value,
    };
  });

  const attributeColumns = getAttributeArrayForLeadAttribute(tableAttribute).map(attribute =>
    createAttributeColumn(attribute)
  );
  const markdownVsCategoryColumn = createMeasureColumn(markdownVsCategory, {
    header: {
      mainHeaderId: markdownVsReferenceTitleId,
    },
    signedValue: true,
    meta: {
      headerExporter: exportIntl => exportIntl.formatMessage({ id: markdownVsReferenceTitleId }),
      valueExporter: (value: unknown, exportIntl) =>
        `${exportIntl.formatNumber(getMeasureEntryValue(value as MeasureEntry) * 100, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} p.P.`,
    },
  });
  const priceColumns = [
    createMeasureColumn(avgSoldPrice),
    createMeasureColumn(originalMerchandisePrice),
    createMeasureColumn(avgPriceMarkdown),
    createMeasureColumn(soldAtOriginalPrice),
    {
      ...markdownVsCategoryColumn,
      header: intl.formatMessage({ id: markdownVsReferenceTitleId }),
      cell: markdownVsCategoryCell,
    },
    createMeasureColumn(soldItems),
  ];

  const columns = [...attributeColumns, ...priceColumns] as ColumnDef<PriceBreakdownRow>[];
  const titleId = `entities.name.singular.${attributeToToggleTranslationKey[tableAttribute] ?? tableAttribute}`;

  const getRowClick = () => {
    switch (tableAttribute) {
      case 'styBrandColor':
        return (row: Row<PriceBreakdownRow>) => {
          void navigate(styleLink(row.original.prdNumber ?? '', row.original.styBrandColor ?? '', 'price'));
        };
      case 'artEan':
        return (row: Row<PriceBreakdownRow>) => {
          void navigate(articleLink(row.original.artEan ?? '', 'price'));
        };
      case 'prdNumber':
        return (row: Row<PriceBreakdownRow>) => {
          void navigate(productLink(row.original.prdNumber ?? '', 'price'));
        };
      default:
        return undefined;
    }
  };

  return (
    <>
      <ToggleButtonGroup
        className="mb-auto mt-auto"
        exclusive
        value={tableAttribute}
        onChange={(_event, value: AttributeCode | null) => {
          if (value !== null) setTableAttribute(value);
        }}
      >
        {availableTables.map(attribute => (
          <StyledToggleButton
            key={attribute.code}
            value={attribute.code}
            size="small"
            className="whitespace-nowrap text-xs"
            sx={{ color: '#1A1A1A' }}
          >
            {intl.formatMessage({
              id: `entities.name.singular.${attributeToToggleTranslationKey[attribute.code] ?? attribute.code}`,
            })}
          </StyledToggleButton>
        ))}
      </ToggleButtonGroup>
      <DataTableContextProvider>
        <DataTable
          titleId={titleId}
          rows={rows}
          columns={columns}
          defaultSortingState={[{ id: avgSoldPrice.code, desc: true }]}
          isError={isError}
          onRowClick={getRowClick()}
          exportName={`price-lens-${tableAttribute}`}
          testId={`price-lens-${tableAttribute}-table`}
        />
      </DataTableContextProvider>
    </>
  );
}
